import React, { useMemo, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import { fetchSourceImageBlob } from './imageMarkupUtils.js';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

/** Return the current marker array while accepting legacy single-marker answers. */
function getSelectionMarkers(selection) {
    if (Array.isArray(selection?.markers)) return selection.markers;
    return selection?.marker ? [selection.marker] : [];
}

/** Let a respondent select location images and place markers up to each image limit. */
export default function ImageMarkupQuestion({ formId, question, value, onChange, disabled }) {
    const images = useMemo(() => question?.imageMarkupSettings?.images || [], [question]);
    const answers = value && typeof value === 'object' ? value : {};
    const [sourceUrls, setSourceUrls] = useState({});
    const [activeImageId, setActiveImageId] = useState('');
    const [loadingImageIds, setLoadingImageIds] = useState({});

    /** Record whether one source image is currently being downloaded. */
    function setImageLoading(imageId, isLoading) {
        setLoadingImageIds((current) => {
            const next = { ...current };
            if (isLoading) next[imageId] = true;
            else delete next[imageId];
            return next;
        });
    }

    /** Download and cache a source image, returning whether it is ready to use. */
    async function ensureImageLoaded(image) {
        if (sourceUrls[image.imageId]?.objectUrl) return true;
        if (loadingImageIds[image.imageId]) return false;

        setImageLoading(image.imageId, true);
        try {
            const blob = await fetchSourceImageBlob(formId, image.fileId);
            setSourceUrls((current) => ({
                ...current,
                [image.imageId]: { blob, objectUrl: URL.createObjectURL(blob) },
            }));
            return true;
        } catch (error) {
            console.error('Failed to load Forms source image', error);
            toast.error('❌ This location image is not yet available. Please try again shortly.', TOAST_CONFIG);
            return false;
        } finally {
            setImageLoading(image.imageId, false);
        }
    }

    async function toggleImage(image) {
        const selected = Boolean(answers[image.imageId]);
        if (selected) {
            const next = { ...answers };
            delete next[image.imageId];
            onChange(next);
            if (activeImageId === image.imageId) setActiveImageId('');
            return;
        }
        const maximumSelections = Number(question?.fileSettings?.maxFiles) || 10;
        if (Object.keys(answers).length >= maximumSelections) {
            toast.error(`❌ Select no more than ${maximumSelections} location images for this question.`, TOAST_CONFIG);
            return;
        }
        const imageLoaded = await ensureImageLoaded(image);
        if (!imageLoaded) return;
        onChange({ ...answers, [image.imageId]: { markers: [] } });
        setActiveImageId(image.imageId);
    }

    function placeMarker(event, imageId) {
        if (disabled) return;
        const imageDefinition = images.find((image) => image.imageId === imageId);
        const maximumMarkers = Math.max(1, Number(imageDefinition?.maxMarkers) || 1);
        const currentMarkers = getSelectionMarkers(answers[imageId]);
        if (currentMarkers.length >= maximumMarkers) {
            toast.error(`❌ This image allows a maximum of ${maximumMarkers} ${maximumMarkers === 1 ? 'marker' : 'markers'}.`, TOAST_CONFIG);
            return;
        }
        const bounds = event.currentTarget.getBoundingClientRect();
        const marker = {
            x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
            y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height)),
        };
        onChange({ ...answers, [imageId]: { markers: [...currentMarkers, marker] } });
    }

    /** Remove one marker without allowing its click to add another marker. */
    function removeMarker(event, imageId, markerIndex) {
        event.stopPropagation();
        if (disabled) return;
        const nextMarkers = getSelectionMarkers(answers[imageId]).filter((_, index) => index !== markerIndex);
        onChange({ ...answers, [imageId]: { markers: nextMarkers } });
    }

    const selectedImages = images.filter((image) => answers[image.imageId]);
    const activeImage = selectedImages.find((image) => image.imageId === activeImageId) || selectedImages[0];
    const activeSource = activeImage ? sourceUrls[activeImage.imageId] : null;
    const activeMarkers = activeImage ? getSelectionMarkers(answers[activeImage.imageId]) : [];
    const activeMaximumMarkers = Math.max(1, Number(activeImage?.maxMarkers) || 1);

    return <div className="forms-image-markup-question">
        <div className="forms-image-choice-grid">
            {images.map((image) => {
                const selected = Boolean(answers[image.imageId]);
                const imageLoading = Boolean(loadingImageIds[image.imageId]);
                return <button type="button" key={image.imageId} disabled={disabled || imageLoading} aria-busy={imageLoading} aria-label={imageLoading ? `Loading ${image.name}` : undefined} className={selected ? 'forms-image-choice forms-image-choice--selected' : 'forms-image-choice'} onClick={() => toggleImage(image)}>
                    {imageLoading ? <CgSpinner className="forms-image-loading-spinner" aria-hidden="true" /> : <>
                        <span>{selected ? 'Selected' : 'Select'}</span>
                        <strong>{image.name}</strong>
                        <small>Up to {Math.max(1, Number(image.maxMarkers) || 1)} {Math.max(1, Number(image.maxMarkers) || 1) === 1 ? 'marker' : 'markers'}</small>
                    </>}
                </button>;
            })}
        </div>
        {selectedImages.length > 1 && <div className="forms-image-tabs">{selectedImages.map((image) => {
            const imageLoading = Boolean(loadingImageIds[image.imageId]);
            return <button type="button" key={image.imageId} disabled={disabled || imageLoading} aria-busy={imageLoading} aria-label={imageLoading ? `Loading ${image.name}` : undefined} className={activeImage?.imageId === image.imageId ? 'active' : ''} onClick={async () => {
                const imageLoaded = await ensureImageLoaded(image);
                if (imageLoaded) setActiveImageId(image.imageId);
            }}>
                {imageLoading ? <CgSpinner className="forms-image-loading-spinner" aria-hidden="true" /> : image.name}
            </button>;
        })}</div>}
        {activeImage && <div className="forms-image-marker-editor">
            <div className="forms-image-marker-guidance">
                <p>Click or tap the image to add a red marker. Click or tap a marker to remove it.</p>
                <strong>{activeMarkers.length} of {activeMaximumMarkers} {activeMaximumMarkers === 1 ? 'marker' : 'markers'} placed</strong>
            </div>
            {activeSource?.objectUrl ? <div className="forms-image-marker-canvas" onClick={(event) => placeMarker(event, activeImage.imageId)} role="button" tabIndex={disabled ? -1 : 0} aria-label={`Mark ${activeImage.name}`}>
                <img src={activeSource.objectUrl} alt={activeImage.name} />
                {activeMarkers.map((marker, markerIndex) => <button
                    type="button"
                    key={`${activeImage.imageId}-marker-${markerIndex}`}
                    className="forms-image-marker-dot"
                    style={{ left: `${marker.x * 100}%`, top: `${marker.y * 100}%` }}
                    disabled={disabled}
                    aria-label={`Remove marker ${markerIndex + 1} from ${activeImage.name}`}
                    onClick={(event) => removeMarker(event, activeImage.imageId, markerIndex)}
                />)}
            </div> : <button type="button" className="forms-secondary-button forms-image-load-button" disabled={disabled || Boolean(loadingImageIds[activeImage.imageId])} aria-busy={Boolean(loadingImageIds[activeImage.imageId])} onClick={() => ensureImageLoaded(activeImage)}>
                {loadingImageIds[activeImage.imageId] ? <CgSpinner className="forms-image-loading-spinner" aria-label={`Loading ${activeImage.name}`} /> : `Load ${activeImage.name}`}
            </button>}
        </div>}
    </div>;
}
